<script setup>
import { i18n } from 'boot/i18n';
import {
  HrAdaptiveFormDialog,
  HrAvatar,
  HrAssistChip,
  HrIcon,
  HrItemLabel,
  HrList,
  HrListItem,
  HrListSectionHeader,
  HrTextField,
} from 'src/design-system';
import { computed, ref, watch } from 'vue';

/**
 * Find a person, or say you want to create one.
 *
 * DELIBERATELY KNOWS NOTHING ABOUT WHY. No policyholder, no coverage, no relationship, no
 * demographics. It takes rows, filters them by name, and reports either the row that was chosen or
 * the text that was typed. Everything domain-shaped is the host's: which people are relevant, how a
 * row's supporting line reads, and what happens when somebody asks to create one.
 *
 * That is why it lives here for now rather than being extracted. It has one consumer, and the
 * contexts guidance is to keep a shape inside the context that needs it and move it when a second
 * one genuinely pushes back. Nothing in this file imports the domain layer, so that move is a folder
 * rename and a barrel line.
 *
 * MATCHING IS SUBSTRING, NOT PREFIX, and that is the whole anti-duplicate mechanism rather than a
 * nicety. Somebody about to add a second David Andersen has to see the first one, and a prefix match
 * would hide him behind a differently-spelled surname. It also means the wider set cannot be a
 * query: Firestore does prefix ranges and nothing looser, so the host hands over a bounded set and
 * the filtering happens here.
 *
 * THE CREATE ROW CANNOT BE REACHED WITHOUT TYPING. Typing a name IS the search, so by the time
 * create is offered the near matches are already on screen. An always-visible create button
 * elsewhere would break that, which is why there is no such button.
 *
 * @interfaceShape imperative
 */
defineOptions({ name: 'PersonPicker' });

/**
 * @typedef {Object} PersonPickerRow
 * @property {string} id
 * @property {string} name - matched against, and shown as the row's label
 * @property {string|null} [caption] - supporting line; the host decides what identifies a person
 * @property {string|null} [warning] - short emphasised prefix on the caption, e.g. a missing detail
 * @property {string|null} [badge] - short chip after the name, e.g. to mark the subject themselves
 * @property {string|null} [initials]
 */

const props = defineProps({
  /** Sheet and dialog title. The host owns it, since only the host knows what is being chosen. */
  title: {
    type: String,
    required: true,
  },
  /**
   * Rows worth offering before anything is typed: bounded, and ordered by the host.
   * @type {PersonPickerRow[]}
   */
  suggested: {
    type: Array,
    default: () => [],
  },
  /**
   * The wider set, searched only once enough has been typed. Never browsable, so it is not shown
   * until there is a query to narrow it.
   * @type {PersonPickerRow[]}
   */
  others: {
    type: Array,
    default: () => [],
  },
  /** Whether the wider set is still arriving, which draws placeholder rows in its place. */
  isLoadingOthers: {
    type: Boolean,
    default: false,
  },
  /**
   * Whether the suggested set is still arriving. Separate from `isLoadingOthers` because the two
   * are read at different moments: this one can still be out when the picker opens, and a section
   * that renders nothing at all says nobody is worth offering.
   */
  isLoadingSuggested: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  /** A row was chosen. Carries the row, not just its id, so the host need not look it up again. */
  select: null,
  /** Somebody asked to create a person under the typed name. Carries the trimmed text. */
  create: null,
  /**
   * Enough has been typed that the wider set is now in play. The host reads it when this fires,
   * rather than on open, so opening the picker to choose the obvious candidate costs nothing.
   */
  search: null,
});

const dialog = ref(null);
const query = ref('');

defineExpose({
  // Cleared here rather than on close. Carrying the last search over would show yesterday's
  // shortlist and offer to create somebody already dealt with, and the dialog this wraps stays
  // mounted between uses, so there is no teardown to hang the reset on.
  show: () => {
    query.value = '';
    dialog.value?.show();
  },
  hide: () => dialog.value?.hide(),
});

/**
 * How much has to be typed before the wider set is searched and creating is offered.
 *
 * Not zero, because an unnarrowed clinic list is the browsable directory this is not meant to be,
 * and because one character is not a name. Two is a placeholder: the floor for creating a person is
 * an open product question, and when it is answered this is where it lands.
 */
const MIN_QUERY = 2;

/** Placeholder rows while a set loads. Enough to read as a list, few enough to not mislead. */
const PENDING_ROWS = 2;

const trimmedQuery = computed(() => query.value.trim());
const isSearching = computed(() => trimmedQuery.value.length >= MIN_QUERY);

const matches = (row) => {
  return row.name?.toLowerCase().includes(trimmedQuery.value.toLowerCase()) === true;
};

const suggestedRows = computed(() => {
  return isSearching.value ? props.suggested.filter(matches) : props.suggested;
});

// Empty until there is a query, then filtered. The host may hand over the whole clinic; withholding
// it here is what keeps this from reading as a directory.
const otherRows = computed(() => {
  return isSearching.value ? props.others.filter(matches) : [];
});

const isLoadingWiderSet = computed(() => isSearching.value && props.isLoadingOthers);

const createLabel = computed(() => {
  return i18n.t('personPicker.actionCreate', { name: trimmedQuery.value });
});

function onSelect(row) {
  emit('select', row);
  dialog.value?.hide();
} // onSelect()

function onCreate() {
  emit('create', trimmedQuery.value);
  dialog.value?.hide();
} // onCreate()

// Fires on the transition, not on every keystroke. The host is expected to read once and match
// locally afterwards, so repeating this per character would say something untrue about the need.
watch(isSearching, (searching) => {
  if (searching) {
    emit('search');
  }
});
</script>

<template>
  <hr-adaptive-form-dialog
    ref="dialog"
    :title="title"
    size="sm"
    no-refocus
    data-e2e="dialog-person-picker"
  >
    <!-- Autofocus because typing IS the search: the surface exists to be typed into, and the first
         thing anybody does here is start a name. Falls through to the underlying input rather than
         being a field prop, which is why it is not documented on HrTextField. -->
    <hr-text-field
      v-model="query"
      autofocus
      :placeholder="i18n.t('personPicker.searchPlaceholder')"
      clearable
      hide-bottom-space
      data-e2e="input-person-search"
    >
      <template #prepend>
        <hr-icon name="search" />
      </template>
    </hr-text-field>

    <hr-list>
      <!-- Shown while the shortlist is still arriving as well as once it has, because a section
           that renders nothing is the picker saying nobody is worth offering. Not gated on having
           typed, unlike the wider set below: this is what the picker opens showing. -->
      <template v-if="suggestedRows.length > 0 || isLoadingSuggested">
        <hr-list-section-header>
          {{ i18n.t('personPicker.groupSuggested') }}
        </hr-list-section-header>
        <hr-list-item
          v-for="row in suggestedRows"
          :key="row.id"
          avatar
          clickable
          :label="row.name"
          data-e2e="list-item-person"
          @click="onSelect(row)"
        >
          <template #leading>
            <hr-avatar size="sm">
              {{ row.initials }}
            </hr-avatar>
          </template>
          <template #default>
            <hr-item-label>
              {{ row.name }}
              <hr-assist-chip
                v-if="row.badge"
                size="sm"
                :label="row.badge"
              />
            </hr-item-label>
            <hr-item-label
              v-if="row.warning || row.caption"
              caption
            >
              <!-- The design system's own role class, not a local colour. It is filed under
                   "escape hatches" there, which is the honest name for it: the caveat is a
                   property of the label and there is no component-level way to say so yet. -->
              <span
                v-if="row.warning"
                class="hr-text-warning"
              >{{ row.warning }}</span>
              {{ row.caption }}
            </hr-item-label>
          </template>
        </hr-list-item>
        <hr-list-item
          v-for="index in isLoadingSuggested ? PENDING_ROWS : 0"
          :key="`pending-suggested-${index}`"
          pending
          avatar
          label=""
          caption=""
        >
          <template #leading>
            <hr-avatar
              size="sm"
              pending
            />
          </template>
        </hr-list-item>
      </template>

      <template v-if="isSearching && (otherRows.length > 0 || isLoadingWiderSet)">
        <hr-list-section-header>
          {{ i18n.t('personPicker.groupClinic') }}
        </hr-list-section-header>
        <hr-list-item
          v-for="row in otherRows"
          :key="row.id"
          avatar
          clickable
          :label="row.name"
          :caption="row.caption"
          data-e2e="list-item-person"
          @click="onSelect(row)"
        >
          <template #leading>
            <hr-avatar size="sm">
              {{ row.initials }}
            </hr-avatar>
          </template>
        </hr-list-item>
        <hr-list-item
          v-for="index in isLoadingWiderSet ? PENDING_ROWS : 0"
          :key="`pending-${index}`"
          pending
          avatar
          label=""
          caption=""
        >
          <template #leading>
            <hr-avatar
              size="sm"
              pending
            />
          </template>
        </hr-list-item>
      </template>

      <!-- The only way to reach creation, so it is tied to having typed. It also stands in for an
           empty-state message: when nothing matched, a single offered action says "nobody, and here
           is what you can do" without a line of prose repeating the row beneath it. -->
      <hr-list-item
        v-if="isSearching"
        clickable
        icon="add"
        :label="createLabel"
        :caption="i18n.t('personPicker.actionCreateHint')"
        data-e2e="list-item-create-person"
        @click="onCreate"
      />
    </hr-list>
  </hr-adaptive-form-dialog>
</template>
